import { ActionCreator } from "redux";

import {
	ActionType,
	LoginStatusUpdatedAction,
	DateUpdatedAction,
	TablesUpdatedAction,
	PanicAction,
	ReservationsUpdatedAction,
	SingleReservationUpdatedAction,
	AddReservationDialogOpenedAction,
	AddReservationDialogClosedAction,
	ReservationAddedAction,
	ReservationRemovedAction,
	EditReservationDialogOpenedAction,
	OccupiedDaysUpdatedAction,
	TableLinkModeActivatedAction,
	TableLinkModeDeactivatedAction,
	TableLinkDeletionModeActivatedAction,
	TableLinkTargetGroupSetAction,
	TableLinkTableAddedAction,
	TableLinkTableRemovedAction,
	TableLinkGroupsUpdatedAction,
	SingleOccupiedDayUpdatedAction,
	AreasUpdatedAction,
	LayoutChangedAction,
	CapabilitiesUpdatedAction,
	InquiryAddedAction,
	InquiriesUpdatedAction,
	SingleInquiryUpdatedAction,
	InquiryRemovedAction,
	AuthInfoUpdatedAction,
	PaymentsUpdatedAction,
	SinglePaymentUpdatedAction,
	AddPaymentDialogClosedAction,
	AddPaymentDialogOpenedAction,
	EditInquiryDialogClosedAction,
	EditInquiryDialogOpenedAction
} from "./type";
import { Table } from "../../api/tables";
import { Reservation } from "../../api/reservations";
import { OccupiedDay } from "../../api/occupied-days";
import { Area } from "../../api/areas";
import { Capabilities } from "../../integration/capabilities";
import { Inquiry } from "../../api/inquiries";
import { AuthInfo } from "../../api/token-storage";
import { Payment } from '../../api/payments';

export const setLoginStatus: ActionCreator<LoginStatusUpdatedAction> = (
	status: boolean
) => ({
	type: ActionType.AppLoginStatusUpdated,
	payload: {
		status
	}
});

export const updateAuthInfo: ActionCreator<AuthInfoUpdatedAction> = (
	authInfo: AuthInfo
) => ({
	type: ActionType.AppAuthInfoUpdated,
	payload: {
		authInfo
	}
});

export const setDate: ActionCreator<DateUpdatedAction> = (date: Date) => ({
	type: ActionType.AppDateUpdated,
	payload: {
		date
	}
});

export const updateTables: ActionCreator<TablesUpdatedAction> = (
	tables: Table[]
) => ({
	type: ActionType.AppTablesUpdated,
	payload: {
		tables: tables ? tables : []
	}
});

export const updateAreas: ActionCreator<AreasUpdatedAction> = (
	areas: Area[]
) => ({
	type: ActionType.AppAreasUpdated,
	payload: {
		areas: areas ? areas : []
	}
});

export const updateReservations: ActionCreator<ReservationsUpdatedAction> = (
	reservations: Reservation[]
) => ({
	type: ActionType.AppReservationsUpdated,
	payload: {
		reservations: reservations ? reservations : []
	}
});

export const updateSingleReservation: ActionCreator<
	SingleReservationUpdatedAction
> = (id: string, reservation: Reservation) => ({
	type: ActionType.AppSingleReservationUpdated,
	payload: {
		id,
		reservation
	}
});

export const addReservation: ActionCreator<ReservationAddedAction> = (
	reservation: Reservation
) => ({
	type: ActionType.AppReservationAdded,
	payload: {
		reservation
	}
});

export const removeReservation: ActionCreator<ReservationRemovedAction> = (
	id: string
) => ({
	type: ActionType.AppReservationRemoved,
	payload: {
		id
	}
});

export const addInquiry: ActionCreator<InquiryAddedAction> = (
	inquiry: Inquiry
) => ({
	type: ActionType.AppInquiryAdded,
	payload: {
		inquiry
	}
});

export const updateInquiries: ActionCreator<InquiriesUpdatedAction> = (
	inquiries: Inquiry[]
) => ({
	type: ActionType.AppInquiriesUpdated,
	payload: {
		inquiries: inquiries ? inquiries : []
	}
});

export const updateSingleInquiry: ActionCreator<SingleInquiryUpdatedAction> = (
	id: string,
	inquiry: Inquiry
) => ({
	type: ActionType.AppSingleInquiryUpdated,
	payload: {
		id,
		inquiry
	}
});

export const removeInquiry: ActionCreator<InquiryRemovedAction> = (
	id: string
) => ({
	type: ActionType.AppInquiryRemoved,
	payload: {
		id
	}
});

export const updateOccupiedDays: ActionCreator<OccupiedDaysUpdatedAction> = (
	days: OccupiedDay[]
) => ({
	type: ActionType.AppOccupiedDaysUpdated,
	payload: {
		days
	}
});

export const updateSingleOccupiedDay: ActionCreator<
	SingleOccupiedDayUpdatedAction
> = (id: string, updateData: OccupiedDay) => ({
	type: ActionType.AppSingleOccupiedDayUpdated,
	payload: {
		id,
		updateData
	}
});

export const updatePayments: ActionCreator<
	PaymentsUpdatedAction
> = (payments: Payment[], page: number, count: number) => ({
	type: ActionType.AppPaymentsUpdated,
	payload: {
		payments,
		page,
		count
	}
});

export const updateSinglePayment: ActionCreator<
	SinglePaymentUpdatedAction
> = (id: string, updateData: Payment) => ({
	type: ActionType.AppSinglePaymentUpdated,
	payload: {
		id,
		updateData
	}
});

export const changeLayout: ActionCreator<LayoutChangedAction> = (
	layout: "desktop" | "tablet" | "phone"
) => ({
	type: ActionType.AppLayoutChanged,
	payload: {
		layout
	}
});

export const panic: ActionCreator<PanicAction> = () => ({
	type: ActionType.AppPaniced
});

export const openAddReservationDialog: ActionCreator<
	AddReservationDialogOpenedAction
> = () => ({
	type: ActionType.AppAddReservationDialogOpened
});

export const closeAddReservationDialog: ActionCreator<
	AddReservationDialogClosedAction
> = () => ({
	type: ActionType.AppAddReservationDialogClosed
});

export const openEditReservationDialog: ActionCreator<
	EditReservationDialogOpenedAction
> = (reservation: Reservation) => ({
	type: ActionType.AppEditReservationDialogOpened,
	payload: {
		reservation
	}
});

export const openAddPaymentDialog: ActionCreator<
	AddPaymentDialogOpenedAction
> = (inquiryId?: string, email?: string, name?: string, amount?: string, validHours?: number, message?: string, paymentId?: string) => ({
	type: ActionType.AppAddPaymentDialogOpened,
	payload: {
		inquiryId,
		email,
		name,
		amount,
		validHours,
		message,
		paymentId
	}
});

export const closeAddPaymentDialog: ActionCreator<
	AddPaymentDialogClosedAction
> = () => ({
	type: ActionType.AppAddPaymentDialogClosed
});

export const openEditInquiryDialog: ActionCreator<
	EditInquiryDialogOpenedAction
> = (inquiry: Inquiry) => ({
	type: ActionType.AppEditInquiryDialogOpened,
	payload: {
		inquiry
	}
});

export const closeEditInquiryDialog: ActionCreator<
	EditInquiryDialogClosedAction
> = () => ({
	type: ActionType.AppEditInquiryDialogClosed
});

export const activateTableLinkMode: ActionCreator<
	TableLinkModeActivatedAction
> = () => ({
	type: ActionType.AppTableLinkModeActivated
});

export const deactivateTableLinkMode: ActionCreator<
	TableLinkModeDeactivatedAction
> = () => ({
	type: ActionType.AppTableLinkModeDeactivated
});

export const activateTableLinkDeletionMode: ActionCreator<
	TableLinkDeletionModeActivatedAction
> = () => ({
	type: ActionType.AppTableLinkDeletionModeActivated
});

export const setTableLinkTargetGroup: ActionCreator<
	TableLinkTargetGroupSetAction
> = (index: number) => ({
	type: ActionType.AppTableLinkTargetGroupSet,
	payload: {
		index
	}
});

export const addTableToLinkGroup: ActionCreator<TableLinkTableAddedAction> = (
	id: string
) => ({
	type: ActionType.AppTableLinkTableAdded,
	payload: {
		id
	}
});

export const removeTableFromLinkGroup: ActionCreator<
	TableLinkTableRemovedAction
> = (id: string) => ({
	type: ActionType.AppTableLinkTableRemoved,
	payload: {
		id
	}
});

export const updateTableLinkGroups: ActionCreator<
	TableLinkGroupsUpdatedAction
> = (groups: string[][]) => ({
	type: ActionType.AppTableLinkGroupsUpdated,
	payload: {
		groups
	}
});

export const updateCapabilities: ActionCreator<CapabilitiesUpdatedAction> = (
	capabilities: Capabilities
) => ({
	type: ActionType.AppCapabilitiesUpdated,
	payload: {
		capabilities
	}
});
